import {
	isStoreAssistant,
	isStoreFollowUp,
	type StoreChatItem,
	type StoreFollowUp,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getLastAgentMessageID } from './getLastAgentMessageID';

/**
 * Re-attach client-only FOLLOW_UP chips to a transcript that was just rebuilt from the server.
 *
 * Follow-ups arrive only as a `FOLLOW_UP_QUERIES` stream part and are never persisted, so any
 * genuine refetch would otherwise blank them (see TREX-2174). They are only re-attached when the
 * server still ends the conversation on the exact assistant message they were generated for, so a
 * turn added in another tab correctly drops them rather than stranding stale chips under a newer
 * answer.
 */
export const preserveTrailingFollowUps = (
	previousMessages: StoreChatItem[],
	networkMessages: StoreChatItem[],
): StoreChatItem[] => {
	// Only the contiguous run at the very end is eligible. A FOLLOW_UP stranded mid-transcript is
	// already an invariant violation; do not propagate it.
	let firstTrailingIndex = previousMessages.length;
	while (firstTrailingIndex > 0 && isStoreFollowUp(previousMessages[firstTrailingIndex - 1])) {
		firstTrailingIndex--;
	}
	const trailingFollowUps = previousMessages.slice(firstTrailingIndex) as StoreFollowUp[];
	if (trailingFollowUps.length === 0) {
		return networkMessages;
	}

	// The server transcript must still end on an assistant turn...
	const lastNetworkMessage = networkMessages.at(-1);
	if (!lastNetworkMessage || !isStoreAssistant(lastNetworkMessage)) {
		return networkMessages;
	}

	// ...and it must be the same assistant message these follow-ups were generated for.
	const anchorMessageId =
		trailingFollowUps[0].agentMessageId ?? getLastAgentMessageID(previousMessages);
	if (!anchorMessageId || anchorMessageId !== getLastAgentMessageID(networkMessages)) {
		return networkMessages;
	}

	return [...networkMessages, ...trailingFollowUps];
};
